export interface DiagramCollisionLayout {
  centers: number[];
  width: number;
}

interface DiagramCollisionOptions {
  left: number;
  right: number;
  preferredWidth: number;
  gap: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Keep diagrams close to their score anchors while guaranteeing that their
 * horizontal footprints do not overlap inside the system.
 */
export function layoutDiagramCenters(
  anchors: number[],
  { left, right, preferredWidth, gap }: DiagramCollisionOptions,
): DiagramCollisionLayout {
  if (anchors.length === 0) return { centers: [], width: preferredWidth };

  const available = Math.max(0, right - left);
  const effectiveGap =
    anchors.length > 1 ? Math.min(Math.max(0, gap), available / (anchors.length - 1)) : 0;
  const maximumWidth =
    anchors.length > 0
      ? Math.max(0, (available - effectiveGap * (anchors.length - 1)) / anchors.length)
      : preferredWidth;
  const width = Math.min(Math.max(0, preferredWidth), maximumWidth);
  const minimumDistance = width + effectiveGap;
  const minimumCenter = left + width / 2;
  const maximumCenter = right - width / 2;
  const centers = anchors.map((anchor) => clamp(anchor, minimumCenter, maximumCenter));

  // Push a cluster forwards, then pull it back from the right edge. When the
  // system has enough room, the two passes satisfy both bounds and spacing.
  for (let index = 1; index < centers.length; index += 1) {
    centers[index] = Math.max(centers[index], centers[index - 1] + minimumDistance);
  }
  if (centers[centers.length - 1] > maximumCenter) {
    centers[centers.length - 1] = maximumCenter;
    for (let index = centers.length - 2; index >= 0; index -= 1) {
      centers[index] = Math.min(centers[index], centers[index + 1] - minimumDistance);
    }
  }

  return { centers, width };
}
